import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Container,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../../layouts/SuperAdminLayout';
import api from '../../../utils/api';

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const superAdminId = user?.id;

  useEffect(() => {
    if (superAdminId) fetchCustomers();
  }, [superAdminId]);

  const fetchCustomers = async () => {
    try {
      const res = await api.get(`/api/customers/get-all/${superAdminId}`);
      setCustomers(res.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCustomer(null);
  };

  const handleUpdate = async () => {
    try {
      await api.put(
        `/api/customers/update/${selectedCustomer._id}`,
        selectedCustomer
      );
      alert('Customer updated!');
      fetchCustomers();
      handleClose();
    } catch (err) {
      console.error('Update failed:', err);
      alert('Update failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete?')) return;
    try {
      await api.delete(`/api/customers/delete/${id}`);
      fetchCustomers();
    } catch (err) {
      alert('Customer could not be deleted!');
      console.error('Delete failed:', err);
    }
  };

  if (!superAdminId) {
    return (
      <SuperAdminLayout>
        <Container sx={{ mt: 4 }}>
          <Typography variant="h6" color="error">
            SuperAdmin ID not found. Please log in again.
          </Typography>
        </Container>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        mt={4}
      >
        <Typography variant="h5">Customer List</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/super-admin/customers/create')}
        >
          Create Customer
        </Button>
      </Box>

      {/* Update Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Update Customer</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            label="Party Name"
            value={selectedCustomer?.partyName || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                partyName: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            label="Party Marka"
            value={selectedCustomer?.partyMarka || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                partyMarka: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            label="Address"
            value={selectedCustomer?.address || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                address: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            label="Transporter Name"
            value={selectedCustomer?.transporterName || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                transporterName: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            label="Transporter Address"
            value={selectedCustomer?.transporterAddress || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                transporterAddress: e.target.value,
              })
            }
            fullWidth
          />

          <TextField
            select
            label="Opening Balance Type"
            value={selectedCustomer?.openingBalanceType || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                openingBalanceType: e.target.value,
              })
            }
            fullWidth
          >
            <MenuItem value="debit">Debit</MenuItem>
            <MenuItem value="credit">Credit</MenuItem>
          </TextField>

          <TextField
            label="Opening Balance"
            value={selectedCustomer?.openingBalance || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                openingBalance: e.target.value,
              })
            }
            fullWidth
          />

          {/* ✅ Payment Type Dropdown */}
          <TextField
            select
            label="Payment Type"
            value={selectedCustomer?.paymentType || ''}
            onChange={(e) =>
              setSelectedCustomer({
                ...selectedCustomer,
                paymentType: e.target.value,
              })
            }
            fullWidth
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="Credit">Credit</MenuItem>
          </TextField>

          {/* ✅ Conditional fields for Credit */}
          {selectedCustomer?.paymentType === 'Credit' && (
            <>
              <TextField
                label="Credit Amount"
                type="number"
                value={selectedCustomer?.creditBalance || ''}
                onChange={(e) =>
                  setSelectedCustomer({
                    ...selectedCustomer,
                    creditBalance: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                label="Credit Time (in days)"
                type="number"
                value={selectedCustomer?.creditTime || ''}
                onChange={(e) =>
                  setSelectedCustomer({
                    ...selectedCustomer,
                    creditTime: e.target.value,
                  })
                }
                fullWidth
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Party Name</TableCell>
              <TableCell>Party Marka</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Transporter Name</TableCell>
              <TableCell>Transporter Address</TableCell>
              <TableCell>Payment Type</TableCell>
              <TableCell>Credit Time</TableCell>
              <TableCell>Credit Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No customers found
                </TableCell>
              </TableRow>
            ) : (
              customers.map((cust, index) => (
                <TableRow key={cust._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{cust.partyName}</TableCell>
                  <TableCell>{cust.partyMarka}</TableCell>
                  <TableCell>{cust.address}</TableCell>
                  <TableCell>{cust.transporterName}</TableCell>
                  <TableCell>{cust.transporterAddress}</TableCell>
                  <TableCell>{cust.paymentType}</TableCell>
                  <TableCell>{cust.creditTime}</TableCell>
                  <TableCell>{cust.creditBalance}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(cust)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(cust._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SuperAdminLayout>
  );
};

export default CustomerList;
